import {
  Users, Briefcase, CalendarBlank, Cube, Stack, DeviceMobile, Wrench,
  FileText, Globe, ChartLineUp, Sparkle, ChartBar, ShieldCheck, CurrencyGbp,
  Handshake, ClipboardText, Gauge, Lightning, Door, Garage, Fire,
  Fingerprint, Wind, Faders, Drop, Truck, Buildings, HardHat,
} from "@phosphor-icons/react";

export const platformModules = [
  { name: "FlowSales", icon: Handshake, desc: "Turn enquiries into won jobs with a pipeline built for field service quoting and follow-up." },
  { name: "FlowService", icon: Wrench, desc: "Plan, schedule and track reactive and planned maintenance across every engineer." },
  { name: "FlowEngineer", icon: DeviceMobile, desc: "A powerful mobile app that gives engineers everything they need on site, online or off." },
  { name: "FlowCheck", icon: ClipboardText, desc: "Dynamic digital jobsheets and inspection forms that adapt to the job in hand." },
  { name: "FlowSafe", icon: ShieldCheck, desc: "Asset management, certificates and compliance kept audit-ready at all times." },
  { name: "FlowFinance", icon: CurrencyGbp, desc: "Invoice on completion, control stock cost and see true job profitability." },
  { name: "FlowAI", icon: Sparkle, desc: "AI insights that spot missed revenue, overdue services and operational bottlenecks." },
];

export const keyFeatures = [
  { name: "Customer Management", icon: Users, desc: "A single source of truth for every customer, site and contact." },
  { name: "Job Management", icon: Briefcase, desc: "Raise, assign and complete jobs without paperwork or double entry." },
  { name: "Operations Calendar", icon: CalendarBlank, desc: "Drag-and-drop scheduling that keeps every engineer fully utilised." },
  { name: "Asset Management", icon: Cube, desc: "Track equipment history, warranties and service dates per site." },
  { name: "Stock Control", icon: Stack, desc: "Know exactly what's on the van and in the store, in real time." },
  { name: "Engineer App", icon: DeviceMobile, desc: "Offline-ready mobile app for jobs, photos, signatures and parts." },
  { name: "Service Plans", icon: Wrench, desc: "Automate planned maintenance so contracts never slip." },
  { name: "Digital Jobsheets", icon: FileText, desc: "Custom forms and certificates completed on site in seconds." },
  { name: "Customer Portal", icon: Globe, desc: "Give clients self-service access to jobs, assets and documents." },
  { name: "Sales Pipeline", icon: ChartLineUp, desc: "Never lose a quote — track every opportunity to close." },
  { name: "AI Insights", icon: Sparkle, desc: "Surface missed revenue and risks before they cost you." },
  { name: "Reporting", icon: ChartBar, desc: "Live dashboards on jobs, engineers, stock and profit." },
];

export const industries = [
  { name: "Electric Gates", icon: Garage },
  { name: "Automatic Doors", icon: Door },
  { name: "Barriers", icon: Faders },
  { name: "Access Control", icon: Fingerprint },
  { name: "Fire & Security", icon: Fire },
  { name: "Facilities Management", icon: Buildings },
  { name: "Electrical Contractors", icon: Lightning },
  { name: "HVAC", icon: Wind },
  { name: "Commercial Maintenance", icon: HardHat },
  { name: "Plumbing", icon: Drop },
  { name: "Forklift Maintenance", icon: Truck },
  { name: "General Field Service", icon: Gauge },
];

export const comparison = {
  generic: {
    label: "Generic CRM",
    points: [
      { text: "Built for any industry", ok: false },
      { text: "Limited job management", ok: false },
      { text: "Separate stock software", ok: false },
      { text: "Separate engineer apps", ok: false },
      { text: "Limited servicing", ok: false },
      { text: "Generic forms", ok: false },
      { text: "Basic reporting", ok: false },
    ],
  },
  flowcore: {
    label: "FlowCore",
    points: [
      { text: "Built specifically for field service", ok: true },
      { text: "Complete operational platform", ok: true },
      { text: "Built-in stock management", ok: true },
      { text: "Integrated engineer app", ok: true },
      { text: "Planned maintenance management", ok: true },
      { text: "Dynamic jobsheets", ok: true },
      { text: "Customer Portal, Asset management & AI insights", ok: true },
    ],
  },
};

export const pricingTiers = [
  {
    name: "Standard",
    price: "£99",
    period: "+VAT / user / month",
    tagline: "For growing field service teams getting organised.",
    popular: false,
    features: [
      "Customer & Job Management",
      "Operations Calendar",
      "Engineer Mobile App",
      "Digital Jobsheets",
      "Stock Control",
      "Standard Reporting",
    ],
    cta: "Book Demo",
  },
  {
    name: "Premium",
    price: "£199",
    period: "+VAT / user / month",
    tagline: "The complete platform for scaling operations.",
    popular: true,
    features: [
      "Everything in Standard",
      "Asset Management",
      "Service Plans & Contracts",
      "Customer Portal",
      "Sales Pipeline",
      "AI Insights & Advanced Reporting",
    ],
    cta: "Book Demo",
  },
  {
    name: "Elite",
    price: "POA",
    period: "with dedicated site guidance",
    tagline: "Tailored onboarding, migration & hands-on support.",
    popular: false,
    features: [
      "Everything in Premium",
      "Dedicated onboarding manager",
      "Data migration service",
      "Custom workflows & integrations",
      "Priority support & SLAs",
      "On-site guidance",
    ],
    cta: "Speak to Sales",
  },
];

export const leanStats = [
  { value: 32, suffix: "%", label: "Less admin time per week" },
  { value: 18, suffix: "%", label: "More jobs completed per engineer" },
  { value: 1000, prefix: "£", suffix: "s", label: "Saved in lost stock & missed sales" },
  { value: 99, suffix: "%", label: "Jobs completed with zero paperwork" },
];

export const featureSections = [
  { key: "crm", title: "CRM", desc: "Every customer, site, contact and asset in one place — so nothing falls through the cracks. Built for the way field service businesses actually work.", benefits: ["360° customer & site history", "No duplicate data entry", "Instant access on any device"] },
  { key: "scheduling", title: "Scheduling", desc: "Drag-and-drop scheduling that matches the right engineer to the right job, first time — reducing wasted travel and idle time.", benefits: ["Smart engineer allocation", "Live route & availability view", "Fewer missed appointments"] },
  { key: "calendar", title: "Operations Calendar", desc: "A single operational view of your entire team, jobs and planned work, so you can run a leaner, tighter operation.", benefits: ["Whole-team visibility", "Colour-coded job status", "Spot capacity at a glance"] },
  { key: "engineers", title: "Engineers", desc: "The FlowEngineer app gives mobile teams everything they need on site — even with no signal — capturing photos, parts and signatures.", benefits: ["Works fully offline", "Photos, parts & signatures", "Real-time sync back to office"] },
  { key: "jobs", title: "Jobs", desc: "Raise, assign, track and invoice jobs without paper. Every job carries its full history, costs and documents.", benefits: ["Paperless job lifecycle", "Live cost tracking", "Invoice on completion"] },
  { key: "assets", title: "Assets", desc: "Track every piece of equipment you service — history, warranties and certificates — ready for the next visit or audit.", benefits: ["Full asset service history", "Warranty & compliance tracking", "QR-code asset lookup"] },
  { key: "stock", title: "Stock", desc: "Built-in stock control across vans and stores, so you stop losing thousands in unaccounted parts.", benefits: ["Van & store stock visibility", "Automatic parts on jobs", "Reduce lost & write-off stock"] },
  { key: "service-plans", title: "Service Plans", desc: "Automate planned maintenance and recurring contracts so servicing revenue never slips again.", benefits: ["Recurring PPM scheduling", "Automatic reminders", "Protect contract revenue"] },
  { key: "jobsheets", title: "Jobsheets", desc: "Dynamic digital jobsheets and certificates that adapt to each job type and complete in seconds on site.", benefits: ["Custom forms per job type", "Compliant certificates", "Instant PDF to customer"] },
  { key: "portal", title: "Customer Portal", desc: "Give customers self-service access to their jobs, assets and documents — reducing inbound calls.", benefits: ["Self-service transparency", "Fewer status calls", "Branded to your business"] },
  { key: "reporting", title: "Reporting", desc: "Live dashboards across jobs, engineers, stock and profitability — decisions backed by real data.", benefits: ["Real-time KPIs", "True job profitability", "Exportable reports"] },
  { key: "ai", title: "AI", desc: "FlowAI surfaces missed revenue, overdue services and operational bottlenecks before they cost you money.", benefits: ["Missed revenue alerts", "Overdue service detection", "Smarter operational insights"] },
];

export const faqs = [
  { q: "Is FlowCore suitable for my industry?", a: "Yes. FlowCore is purpose-built for field service — from plumbing, electricians and gate installers to HVAC, forklift maintenance and mobile engineers of every kind." },
  { q: "How is pricing calculated?", a: "Standard is £99+VAT per user per month and Premium is £199+VAT per user per month. Elite pricing is provided on request and includes dedicated site guidance." },
  { q: "Do you help us migrate our data?", a: "Absolutely. Contact us for onboarding and migration services — our Elite plan includes a dedicated onboarding manager and full data migration." },
  { q: "Does the engineer app work offline?", a: "Yes. The FlowEngineer app is fully offline-ready and syncs automatically once a connection is available." },
  { q: "Can customers see their own jobs?", a: "Yes — the Customer Portal gives your clients self-service access to jobs, assets and documents, reducing inbound calls." },
  { q: "Is there a contract?", a: "We keep things flexible. Book a demo and we'll tailor a plan around your team size and requirements." },
];

export const timeline = [
  { year: "The Problem", text: "Engineers frustrated by clunky, generic software that never fit field service." },
  { year: "The Build", text: "FlowCore designed from real operational experience — by engineers, for engineers." },
  { year: "The Platform", text: "CRM, scheduling, stock, servicing and assets unified into one cloud platform." },
  { year: "The Future", text: "AI-driven insights and an ever-growing ecosystem of field service tools." },
];
